import React, { useState } from 'react';

import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { MailOutline, LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';

import { useLogin } from 'core/login/hooks';

type FormData = {
    email: string;
    password: string;
};

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const {register, handleSubmit, formState: { errors },} = useForm<FormData>({mode: 'onTouched'});
    const {mutate, isLoading} = useLogin({
        onError: (responseError: any) => {
            const errorMessage = responseError instanceof AxiosError ? responseError.response?.data.errors.join(',') || responseError.message : 'Une erreur est survenue.';
            toast.error(errorMessage);
        },
    });
    const onSubmit = (data: FormData) => mutate(data);

    return (
        <>
            <Box
                component='img'
                alt='Chronos'
                src={process.env.PUBLIC_URL + '/hlogo.svg'}
                sx={{ display: { xs: 'block', md: 'none' }, width: 150, mb: 5 }}
            />

            <Typography component='h1' sx={{ fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.02em', color: 'text.primary', mb: 1 }}>
                Bon retour 👋
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', mb: 4 }}>
                Connectez-vous pour accéder à votre espace agence.
            </Typography>

            <Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
                <TextField
                    id='email'
                    label='Adresse email'
                    placeholder='prenom.nom@agence.fr'
                    autoComplete='email'
                    autoFocus
                    margin='none'
                    size='medium'
                    {...register('email', {
                        required: 'Veuillez saisir votre adresse email.',
                        pattern: { value: /\S+@\S+\.\S+/, message: 'Adresse email invalide.' },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message ?? ' '}
                    InputProps={{
                        startAdornment: <InputAdornment position='start'><MailOutline fontSize='small' sx={{ color: 'text.secondary' }} /></InputAdornment>,
                    }}
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    id='password'
                    label='Mot de passe'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    margin='none'
                    size='medium'
                    {...register('password', { required: 'Veuillez saisir votre mot de passe.' })}
                    error={!!errors.password}
                    helperText={errors.password?.message ?? ' '}
                    InputProps={{
                        startAdornment: <InputAdornment position='start'><LockOutlined fontSize='small' sx={{ color: 'text.secondary' }} /></InputAdornment>,
                        endAdornment: (
                            <InputAdornment position='end'>
                                <IconButton
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    onClick={() => setShowPassword((value) => !value)}
                                    edge='end'
                                    size='small'
                                >
                                    {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />
                <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    color='secondary'
                    disabled={isLoading}
                    disableElevation
                    sx={{
                        height: 48,
                        borderRadius: '10px',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 8px 20px rgba(46,107,235,0.28)',
                        '&:hover': { boxShadow: '0 10px 24px rgba(46,107,235,0.36)' },
                    }}
                >
                    {isLoading ? <CircularProgress size={22} color='inherit' /> : 'Se connecter'}
                </Button>
            </Box>

            <Typography sx={{ mt: 4, textAlign: 'center', fontSize: '0.8125rem', color: 'text.secondary' }}>
                Mot de passe oublié ? Contactez votre administrateur Chronos.
            </Typography>
        </>
    )
};

export default Login;
